import React, { useEffect, useState } from 'react';
import { financeiroAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { formatCurrency } from '@/lib/utils';
import { 
  Plus, Play, Trash2, RefreshCw, Settings, ArrowRight,
  Calculator, CheckCircle, AlertCircle, Users, DollarSign
} from 'lucide-react';

export default function RateioPage() {
  const [inputs, setInputs] = useState([]);
  const [saidas, setSaidas] = useState([]);
  const [regras, setRegras] = useState([]);
  const [planoContas, setPlanoContas] = useState([]);
  const [unidades, setUnidades] = useState([]);
  const [segmentos, setSegmentos] = useState([]);
  const [centrosCusto, setCentrosCusto] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filtros
  const [filterCompetencia, setFilterCompetencia] = useState(() => {
    const now = new Date();
    return `${String(now.getMonth() + 1).padStart(2, '0')}/${now.getFullYear()}`;
  });
  
  // Dialog
  const [inputDialogOpen, setInputDialogOpen] = useState(false);
  const [regraDialogOpen, setRegraDialogOpen] = useState(false);
  const [formInput, setFormInput] = useState({
    competencia: '',
    cod_unidade: '',
    cod_conta: '',
    valor_total: ''
  });
  const [formRegra, setFormRegra] = useState({
    cod_conta_rateada: '',
    descricao: '',
    cod_cc_destino: '',
    metodo: 'PROPORC',
    base: 'ALUNOS',
    obs: ''
  });

  useEffect(() => {
    loadCadastros();
  }, []);

  useEffect(() => {
    if (filterCompetencia) {
      loadRateioData();
    }
  }, [filterCompetencia]);

  const loadCadastros = async () => {
    try {
      const [contasRes, unidadesRes, segmentosRes, ccRes, regrasRes] = await Promise.all([
        financeiroAPI.listPlanoContas('DESPESA'),
        financeiroAPI.listUnidades(),
        financeiroAPI.listSegmentos(),
        financeiroAPI.listCentrosCusto(),
        financeiroAPI.listRateioRegras()
      ]);
      setPlanoContas(contasRes.data);
      setUnidades(unidadesRes.data);
      setSegmentos(segmentosRes.data);
      setCentrosCusto(ccRes.data);
      setRegras(regrasRes.data);
    } catch (error) {
      toast.error('Erro ao carregar cadastros');
    }
  };

  const loadRateioData = async () => {
    try {
      setLoading(true);
      const [inputsRes, saidasRes] = await Promise.all([
        financeiroAPI.listRateioInput(filterCompetencia),
        financeiroAPI.listRateioSaida(filterCompetencia)
      ]);
      setInputs(inputsRes.data);
      setSaidas(saidasRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados de rateio');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenInputDialog = () => {
    setFormInput({
      competencia: filterCompetencia,
      cod_unidade: unidades[0]?.cod_unidade || '',
      cod_conta: '',
      valor_total: ''
    });
    setInputDialogOpen(true);
  };

  const handleCreateInput = async () => {
    try {
      if (!formInput.cod_unidade || !formInput.cod_conta || !formInput.valor_total) {
        toast.error('Preencha todos os campos');
        return;
      }
      await financeiroAPI.createRateioInput({
        ...formInput,
        valor_total: parseFloat(formInput.valor_total)
      });
      toast.success('Input de rateio criado');
      setInputDialogOpen(false);
      loadRateioData();
    } catch (error) {
      toast.error('Erro ao criar input');
    }
  };

  const handleDeleteInput = async (id) => {
    if (!window.confirm('Excluir este input?')) return;
    try {
      await financeiroAPI.deleteRateioInput(id);
      toast.success('Input excluído');
      loadRateioData();
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  const handleProcessarRateio = async (unidade) => {
    try {
      const res = await financeiroAPI.processarRateio(filterCompetencia, unidade);
      toast.success(`Rateio processado: ${res.data.results?.length || 0} item(ns)`);
      loadRateioData();
    } catch (error) {
      toast.error('Erro ao processar rateio');
    }
  };

  const handleProcessarTodos = async () => {
    try {
      for (const unidade of unidades) {
        await financeiroAPI.processarRateio(filterCompetencia, unidade.cod_unidade);
      }
      toast.success('Rateio processado para todas as unidades');
      loadRateioData();
    } catch (error) {
      toast.error('Erro ao processar rateio');
    }
  };

  const handleLimparRateio = async () => {
    if (!window.confirm('Limpar todos os rateios desta competência?')) return;
    try {
      await financeiroAPI.limparRateio(filterCompetencia);
      toast.success('Rateios limpos');
      loadRateioData();
    } catch (error) {
      toast.error('Erro ao limpar rateios');
    }
  };

  const handleOpenRegraDialog = () => {
    setFormRegra({
      cod_conta_rateada: '',
      descricao: '',
      cod_cc_destino: '',
      metodo: 'PROPORC',
      base: 'ALUNOS',
      obs: ''
    });
    setRegraDialogOpen(true);
  };

  const handleCreateRegra = async () => {
    try {
      if (!formRegra.cod_conta_rateada || !formRegra.descricao || !formRegra.cod_cc_destino) {
        toast.error('Preencha todos os campos obrigatórios');
        return;
      }
      await financeiroAPI.createRateioRegra(formRegra);
      toast.success('Regra criada');
      setRegraDialogOpen(false);
      loadCadastros();
    } catch (error) {
      toast.error('Erro ao criar regra');
    }
  };

  const handleDeleteRegra = async (id) => {
    if (!window.confirm('Excluir esta regra?')) return;
    try {
      await financeiroAPI.deleteRateioRegra(id);
      toast.success('Regra excluída');
      loadCadastros();
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  const getContaDescricao = (cod) => planoContas.find(c => c.cod_conta === cod)?.descricao || cod;
  const getUnidadeNome = (cod) => unidades.find(u => u.cod_unidade === cod)?.nome_unidade || cod;
  const getSegmentoNome = (cod) => segmentos.find(s => s.cod_seg === cod)?.nome_seg || cod;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Rateio de Despesas</h1>
          <p className="text-muted-foreground">Distribuição automática de custos por segmento</p>
        </div>
        <div className="flex items-center gap-2">
          <Input
            value={filterCompetencia}
            onChange={(e) => setFilterCompetencia(e.target.value)}
            placeholder="MM/YYYY"
            className="w-32"
          />
        </div>
      </div>

      <Tabs defaultValue="inputs" className="space-y-4">
        <TabsList>
          <TabsTrigger value="inputs">Valores a Ratear</TabsTrigger>
          <TabsTrigger value="saidas">Rateios Gerados</TabsTrigger>
          <TabsTrigger value="regras">Regras de Rateio</TabsTrigger>
        </TabsList>

        {/* INPUTS */}
        <TabsContent value="inputs" className="space-y-4">
          <div className="flex justify-between items-center">
            <div className="flex gap-2">
              <Button onClick={handleOpenInputDialog}>
                <Plus className="w-4 h-4 mr-2" />
                Adicionar Valor
              </Button>
              <Button variant="outline" onClick={handleProcessarTodos}>
                <Play className="w-4 h-4 mr-2" />
                Processar Todos
              </Button>
              <Button variant="outline" onClick={handleLimparRateio}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Limpar Rateios
              </Button>
            </div>
          </div>

          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Unidade</TableHead>
                    <TableHead>Conta</TableHead>
                    <TableHead className="text-right">Valor Total</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-[150px]">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8">Carregando...</TableCell>
                    </TableRow>
                  ) : inputs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                        Nenhum valor para ratear nesta competência
                      </TableCell>
                    </TableRow>
                  ) : (
                    inputs.map(input => (
                      <TableRow key={input.id}>
                        <TableCell>{getUnidadeNome(input.cod_unidade)}</TableCell>
                        <TableCell>
                          <span className="font-mono text-xs">{input.cod_conta}</span>
                          <br />
                          <span className="text-xs text-muted-foreground">{getContaDescricao(input.cod_conta)}</span>
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(input.valor_total)}
                        </TableCell>
                        <TableCell>
                          {input.processado ? (
                            <Badge className="bg-green-500">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Processado
                            </Badge>
                          ) : (
                            <Badge variant="outline">
                              <AlertCircle className="w-3 h-3 mr-1" />
                              Pendente
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {!input.processado && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleProcessarRateio(input.cod_unidade)}
                              >
                                <Play className="w-4 h-4" />
                              </Button>
                            )}
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => handleDeleteInput(input.id)}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* SAÍDAS */}
        <TabsContent value="saidas" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Rateios Gerados - {filterCompetencia}</CardTitle>
              <CardDescription>
                {saidas.length} linha(s) de rateio | Total: {formatCurrency(saidas.reduce((acc, s) => acc + s.valor_rateado, 0))}
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Histórico</TableHead>
                    <TableHead>Unidade</TableHead>
                    <TableHead>Segmento</TableHead>
                    <TableHead>CC</TableHead>
                    <TableHead className="text-right">Valor Rateado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {saidas.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                        Nenhum rateio gerado nesta competência
                      </TableCell>
                    </TableRow>
                  ) : (
                    saidas.map(saida => (
                      <TableRow key={saida.id}>
                        <TableCell>{saida.historico}</TableCell>
                        <TableCell>{saida.cod_unidade}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{getSegmentoNome(saida.cod_seg)}</Badge>
                        </TableCell>
                        <TableCell>{saida.cod_cc}</TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(saida.valor_rateado)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* REGRAS */}
        <TabsContent value="regras" className="space-y-4">
          <div className="flex justify-between items-center">
            <Button onClick={handleOpenRegraDialog}>
              <Plus className="w-4 h-4 mr-2" />
              Nova Regra
            </Button>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            {regras.map(regra => (
              <Card key={regra.id} className={`border-l-4 ${regra.ativo ? 'border-l-green-500' : 'border-l-gray-300'}`}>
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-base">{regra.descricao}</CardTitle>
                      <CardDescription className="font-mono text-xs">{regra.cod_conta_rateada}</CardDescription>
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => handleDeleteRegra(regra.id)}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-1">
                      {regra.base === 'ALUNOS' ? (
                        <Users className="w-4 h-4 text-blue-500" />
                      ) : (
                        <DollarSign className="w-4 h-4 text-green-500" />
                      )}
                      <span>Base: {regra.base}</span>
                    </div>
                    <ArrowRight className="w-4 h-4 text-muted-foreground" />
                    <Badge>{regra.cod_cc_destino}</Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Dialog Input */}
      <Dialog open={inputDialogOpen} onOpenChange={setInputDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adicionar Valor para Rateio</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Competência</Label>
              <Input
                value={formInput.competencia}
                onChange={(e) => setFormInput({ ...formInput, competencia: e.target.value })}
                placeholder="MM/YYYY"
              />
            </div>
            <div className="space-y-2">
              <Label>Unidade</Label>
              <Select value={formInput.cod_unidade || undefined} onValueChange={(v) => setFormInput({ ...formInput, cod_unidade: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  {unidades.map(u => (
                    <SelectItem key={u.cod_unidade} value={u.cod_unidade}>{u.nome_unidade}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Conta (Despesa)</Label>
              <Select value={formInput.cod_conta || undefined} onValueChange={(v) => setFormInput({ ...formInput, cod_conta: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione a conta" />
                </SelectTrigger>
                <SelectContent>
                  {planoContas.map(c => (
                    <SelectItem key={c.cod_conta} value={c.cod_conta}>
                      {c.cod_conta} - {c.descricao}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Valor Total</Label>
              <Input
                type="number"
                step="0.01"
                value={formInput.valor_total}
                onChange={(e) => setFormInput({ ...formInput, valor_total: e.target.value })}
                placeholder="0,00"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInputDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleCreateInput}>Adicionar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Regra */}
      <Dialog open={regraDialogOpen} onOpenChange={setRegraDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nova Regra de Rateio</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Conta a Ratear</Label>
              <Select value={formRegra.cod_conta_rateada || undefined} onValueChange={(v) => setFormRegra({ ...formRegra, cod_conta_rateada: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione a conta" />
                </SelectTrigger>
                <SelectContent>
                  {planoContas.map(c => (
                    <SelectItem key={c.cod_conta} value={c.cod_conta}>
                      {c.cod_conta} - {c.descricao}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Descrição</Label>
              <Input
                value={formRegra.descricao}
                onChange={(e) => setFormRegra({ ...formRegra, descricao: e.target.value })}
                placeholder="Ex: Rateio Energia Elétrica"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Base de Rateio</Label>
                <Select value={formRegra.base} onValueChange={(v) => setFormRegra({ ...formRegra, base: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALUNOS">Nº de Alunos</SelectItem>
                    <SelectItem value="RECEITA">Receita Líquida</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>CC Destino</Label>
                <Select value={formRegra.cod_cc_destino || undefined} onValueChange={(v) => setFormRegra({ ...formRegra, cod_cc_destino: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {centrosCusto.map(c => (
                      <SelectItem key={c.cod_cc} value={c.cod_cc}>{c.nome_cc}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRegraDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleCreateRegra}>Criar Regra</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
